import { Response, ResponseOptions } from '@angular/http';
import { Observable } from 'rxjs/Observable';
import { of } from 'rxjs/observable/of';
import { _throw } from 'rxjs/observable/throw';
import { FileUploader, FileUploaderState } from '../../../../app/lib/file-uploader/file-uploader';
import { FileUploaderCordovaHelper } from '../../../../app/lib/file-uploader/file-uploader-cordova.helper';
import { FileUploaderDesktopHelper } from '../../../../app/lib/file-uploader/file-uploader-desktop.helper';
import { VasAppFacade } from '../../../../vas/services/vas-app-facade.service';
import { omit } from '../../../util/utils';
import { VASRestService } from '../../api/vas-rest';
import { FileChangeQItem } from '../file-item';
import { ChangeQItem } from '../item';
import { IOperation } from '../operation.interface';
import { SubResourceOperation } from './sub-resource-operation';

export class FileOperation extends SubResourceOperation implements IOperation {

    transactionType = 'FileOperation';

    constructor(
        public item: FileChangeQItem,
        protected restService: VASRestService,
        protected appFacade: VasAppFacade
    ) {
        super(item, restService, appFacade);
    }

    post(item: FileChangeQItem = this.item): Observable<Response> {
        return this.postJson(item)
            .catch(error => {
                // if the item was already posted we should ignore the error
                if (error.status === 409) {
                    return of(undefined);
                }
                return _throw(error);
            })
            .concatMap(() => {

                if (item.uploadUrl) {
                    return of(item.uploadUrl);
                }

                return FileUploader
                    .fileChangeQItemToUploadUrl(
                        item,
                        this.restService
                    )
                    .do(uploadUrl => {
                        return this.appFacade.changeLog.updateItem(
                            item.key,
                            new FileChangeQItem({...item, uploadUrl})
                        );
                    });
            })
            .concatMap(uploadUrl => {
                const fileUploader = new FileUploader(
                    item.fileSize,
                    item.localFileSystemPath,
                    item.name,
                    item.startChunkNumber,
                    uploadUrl,
                    this.restService,
                    this.appFacade.platform.is('core') ?
                        new FileUploaderDesktopHelper(
                            item,
                            this.restService
                        ) :
                        new FileUploaderCordovaHelper(
                            item,
                            this.restService,
                            this.appFacade.nativeFileService
                        )
                );

                return new Promise((resolve, reject) => {
                    fileUploader.upload().subscribe(
                        // this gets called after every chunk is uploaded
                        info => {
                            // tslint:disable-next-line:switch-default
                            switch (info.state) {
                                case FileUploaderState.ERROR: {
                                    reject(fileUploader.error);
                                    return;
                                }
                                case FileUploaderState.SENDING: {
                                    // we update the ChangeQItem after every
                                    // chunk in case we need to resume
                                    this.appFacade.changeLog
                                        .updateItem(
                                            item.key,
                                            new FileChangeQItem({
                                                ...item,
                                                startChunkNumber: info.chunk
                                            }))
                                        .then(
                                            () => {
                                                // noop
                                            },
                                            () => {
                                                // noop
                                            }
                                        );
                                    return;
                                }
                            }
                        },
                        reject,
                        // when all chunks have been uploaded
                        resolve
                    );
                });
            })
            .map(() => {
                return new Response(new ResponseOptions({
                    body: undefined,
                    status: 201,
                    headers: undefined,
                    url: undefined
                }));

            })
            .catch(error => {

                this.appFacade.errorHandler.handleError(error);

                // If the error is an instance of Error it represents an error
                // that occurred locally, not an error returned from the api.
                // In order for sync manager to properly interpret this we need
                // to make this look like an api 500 error.
                if (error instanceof Error) {
                    return of(new Response(new ResponseOptions({
                        body: `{"message":"${error.message}"}`,
                        status: 500,
                        statusText: error.message,
                        headers: undefined,
                        url: undefined
                    })));
                }
                return of(error);
            });
    }

    postJson(item: ChangeQItem): Observable<Response> {
        return super.post(new ChangeQItem({
            ...item,
            data: omit(item.data, ['fileSize', 'original', 'edited'])
        }));
    }

    patch(item: FileChangeQItem = this.item): Observable<Response> {
        return super.patch(new FileChangeQItem({
            ...item,
            data: omit(item.data, ['fileSize', 'original', 'edited'])
        }));
    }
}



// WEBPACK FOOTER //
// ./src/core/services/sync-up/operations/file-operation.ts